import * as React from 'react';
import type {
  ChangeMetaType,
  ScrubDirection,
  StepDirection,
  UseNumberInput,
  UseNumberInputReturnType,
} from '../_types';
import {
  ALLOWED_KEYS,
  allowedKeysSet,
  CHANGE_META_TYPE,
  NUMBER_INPUT_VARIANTS,
  SCRUB_TO_STEP_MAP,
  STEP_DIRECTIONS,
} from '../_constants';
import {utilities} from '../utilities';
import {useMouseWheel} from './useMouseWheel';
import {mergeRefs} from '../../../utils';
import {useIsomorphicLayoutEffect} from '@packages/systems/spring-hooks';

// ms cooldown for scroll events to prevent rapid changes
const SCROLL_COOLDOWN_MS = 100;

export const useNumberInput = ({
  isDisabled = false,
  isError = false,
  max = Number.MAX_SAFE_INTEGER,
  min = Number.MIN_SAFE_INTEGER,
  name,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  precision = 10,
  prefix,
  ref: externalRef,
  shouldAllowWheelAdjustment = true,
  shouldShowTrailingControls = false,
  skipMultiplier = 10,
  step = 1,
  suffix,
  value,
  variant = NUMBER_INPUT_VARIANTS.DEFAULT,
}: UseNumberInput): UseNumberInputReturnType => {
  const [isScrubbing, setIsScrubbing] = React.useState(false);
  const [isFocused, setIsFocused] = React.useState(false);

  // Tracks the debounce timeout for change end events (wheel or scrub)
  const changeEndDebounceRef = React.useRef<NodeJS.Timeout | null>(null);
  // Tracks if a wheel event has started handle changeType lifecycle
  const scrollStartedRef = React.useRef(false);
  // Ref to track the last time a scroll event was processed
  const lastScrollTimeRef = React.useRef(0);
  // Internal ref is used to encapsulate uncontrolled input value management
  const internalRef = React.useRef<HTMLInputElement | null>(
    null
  ) as React.MutableRefObject<HTMLInputElement | null>;
  // Used to track if the stepper buttons are focused to prevent blur & focus callbacks when clicking on them
  const buttonFocusedRef = React.useRef<boolean>(false);
  // Track if a scrub session has started
  const scrubStartedRef = React.useRef(false);

  // Determine if the current value is the minimum or maximum value based on the provided min and max props.
  const isMin = React.useMemo(
    () => (internalRef?.current?.value && !isDisabled ? value === min : false),
    [min, value, isDisabled]
  );

  const isMax = React.useMemo(
    () => (internalRef?.current?.value && !isDisabled ? value === max : false),
    [max, value, isDisabled]
  );

  const clampInBoundsValue = React.useMemo(
    () => utilities.setInBoundsValue(min, max),
    [min, max]
  );

  useIsomorphicLayoutEffect(() => {
    if (internalRef.current) {
      internalRef.current.value = String(value ?? '');
    }
  }, [value]);

  React.useEffect(() => {
    return () => {
      if (changeEndDebounceRef.current) {
        clearTimeout(changeEndDebounceRef.current);
      }
    };
  }, []);

  // Enhanced clamp function that handles NaN by falling back to min
  const safeClampInBoundsValue = React.useCallback(
    (numericValue: number): number => {
      // If value is NaN, fall back to minimum value
      if (isNaN(numericValue)) {
        return min;
      }
      const clamped = clampInBoundsValue(numericValue);
      // If clampInBoundsValue returns undefined, fall back to min
      return clamped ?? min;
    },
    [clampInBoundsValue, min]
  );

  // Helper function to detect if input contains math expressions
  const containsMathExpression = React.useCallback(
    (inputValue: string): boolean => {
      // Check if the input contains mathematical operators (excluding minus at the start for negative numbers)
      const mathOperatorRegex = /[+*/()]/;
      if (mathOperatorRegex.test(inputValue)) {
        return true;
      }

      const minusIndex = inputValue.indexOf('-');
      // Check for minus sign that's not at the beginning for subtraction
      return minusIndex > 0;
    },
    []
  );

  // Queues a CHANGE_END ChangeType after a debounce period, and sends it to the consumer.
  const queueChangeEndMeta = React.useCallback(
    (endValue: number, callback?: () => void) => {
      if (changeEndDebounceRef.current) {
        clearTimeout(changeEndDebounceRef.current);
      }

      changeEndDebounceRef.current = setTimeout(() => {
        onChange(endValue, {
          changeType: CHANGE_META_TYPE.CHANGE_END,
        });
        callback && callback();
      }, 200);
    },
    [onChange]
  );

  const getSteppedValue = React.useCallback(
    (inputValue: string, direction: StepDirection, isSkip = false) => {
      // Determine the effective step based on whether skipMultiplier is applied
      const effectiveStep = isSkip ? step * skipMultiplier : step;

      // Use applySteppedValue with the effective step and precision
      return utilities.applySteppedValue(
        min,
        max,
        effectiveStep,
        precision
      )(inputValue, direction);
    },
    [min, max, step, skipMultiplier, precision]
  );

  const handleValueChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!internalRef.current) return;

      // Directly update the DOM value
      if (event.target.value === '') {
        if (placeholder && value === undefined) {
          internalRef.current.value = '';
        }
      } else {
        // Only notify parent of change for valid numbers
        // Don't call onChange for empty inputs or invalid intermediate states
        const parsedValue = utilities.parseNumber(event.target.value);
        // Only call onChange if we have a valid number
        // Invalid intermediate states (like "-") are handled internally
        if (!isNaN(parsedValue) && parsedValue !== undefined) {
          onChange(parsedValue, {changeType: CHANGE_META_TYPE.CHANGE});
        }

        internalRef.current.value = event.target.value;
      }
    },
    [onChange, placeholder, value]
  );

  const commitValue = React.useCallback(
    (rawValue: number | undefined, changeType: ChangeMetaType) => {
      let parsed: number;
      if (rawValue !== undefined) {
        parsed = utilities.roundToPrecision(rawValue, precision);
        onChange(parsed, {
          changeType,
        });
      } else {
        // when the value is undefined, we need to call onChange with undefined and set the value to an empty string
        onChange(undefined, {
          changeType,
        });
        internalRef.current!.value = '';
      }
    },
    [onChange, precision]
  );

  // OnBlur handler rounds to percision and checks for out of bounds and calls a passed onBlur if provided
  const handleOnBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      if (
        event.relatedTarget &&
        event.relatedTarget.getAttribute('data-role') === 'number-input-button'
      ) {
        buttonFocusedRef.current = true;
        event.target.focus();
        return;
      }

      setIsFocused(false);

      const inputValue = event.target.value;
      // Handle empty input case
      if (
        inputValue.trim() === '' ||
        isNaN(utilities.parseNumber(inputValue))
      ) {
        // Reset to value on blur if input is empty and there is no placeholder or the value is not undefined
        if (!placeholder && value !== undefined) {
          const resetValueString = String(value);

          event.target.value = resetValueString;
          internalRef.current!.value = resetValueString;

          commitValue(
            clampInBoundsValue(value),
            CHANGE_META_TYPE.CHANGE_CANCEL
          );
        } else {
          commitValue(undefined, CHANGE_META_TYPE.CHANGE_CANCEL);
        }
        onBlur && onBlur();
        return;
      }

      const currentValue = utilities.parseNumber(event.target.value);
      const roundedValue = utilities.roundToPrecision(currentValue, precision);
      const valueToBeCommited = utilities.clampToRange(roundedValue, min, max);

      const clampedValue = clampInBoundsValue(roundedValue);
      const roundValueString = String(valueToBeCommited);

      event.target.value = roundValueString;
      internalRef.current!.value = roundValueString;

      commitValue(clampedValue, CHANGE_META_TYPE.CHANGE);
      onBlur && onBlur?.();
    },
    [
      precision,
      min,
      max,
      clampInBoundsValue,
      commitValue,
      onBlur,
      placeholder,
      value,
    ]
  );

  const handleOnFocus = React.useCallback(
    (event?: React.FocusEvent<HTMLInputElement>) => {
      if (buttonFocusedRef.current) {
        buttonFocusedRef.current = false;
        return;
      }

      setIsFocused(true);
      // Highlight/select the input value on focus
      if (event && event.target && typeof event.target.select === 'function') {
        event.target.select();
      } else if (
        internalRef.current &&
        typeof internalRef.current.select === 'function'
      ) {
        internalRef.current.select();
      }
      onFocus && onFocus?.();
    },
    [onFocus]
  );

  const handleStepClick = React.useCallback(
    (direction: StepDirection, isSkip = false) => {
      if (isDisabled || !internalRef.current) return;

      const input = internalRef.current;
      const currentValueString = input.value;

      // Don't allow stepping when input contains math expressions
      if (containsMathExpression(currentValueString)) {
        return;
      }

      // Only focus and call onFocus if not already focused
      if (document.activeElement !== input) {
        input.focus();
      }

      const newValueString = getSteppedValue(
        currentValueString,
        direction,
        isSkip
      );

      const newValueNumber = utilities.parseNumber(newValueString);

      // Use safe clamp to handle potential NaN values
      const safeValue = safeClampInBoundsValue(newValueNumber);

      input.value = String(safeValue);

      onChange(safeValue, {
        changeType: CHANGE_META_TYPE.CHANGE,
      });
    },
    [
      containsMathExpression,
      getSteppedValue,
      isDisabled,
      onChange,
      safeClampInBoundsValue,
    ]
  );

  // Handles mouse wheel events to increment or decrement the input value.
  // Applies a cooldown to prevent rapid changes and respects focus, disabled state, and min/max bounds.
  const handleWheelEvent = React.useCallback(
    (event: WheelEvent) => {
      if (
        !shouldAllowWheelAdjustment ||
        !internalRef.current ||
        isDisabled ||
        document.activeElement !== internalRef.current ||
        internalRef.current.value === ''
      ) {
        return;
      }

      const currentValueString = internalRef.current.value;

      // Don't allow wheel stepping when input contains math expressions
      if (containsMathExpression(currentValueString)) {
        return;
      }

      // Prevent default scrolling behavior if wheel adjustment is allowed
      event.preventDefault();

      const now = Date.now();
      // Check if the cooldown period has passed since the last scroll event
      // If not, ignore the event to prevent rapid changes
      if (now - lastScrollTimeRef.current < SCROLL_COOLDOWN_MS) return;

      lastScrollTimeRef.current = now;

      const direction =
        event.deltaY < 0 ? STEP_DIRECTIONS.DOWN : STEP_DIRECTIONS.UP;
      const previousValueString = internalRef.current.value;
      const previousValueNumber = utilities.parseNumber(previousValueString);

      // Prevent scrolling if the value is already at min or max
      if (
        (direction === STEP_DIRECTIONS.UP && previousValueNumber >= max) ||
        (direction === STEP_DIRECTIONS.DOWN && previousValueNumber <= min)
      ) {
        return;
      }

      // Trigger `onChange` only when the scroll starts
      if (!scrollStartedRef.current) {
        scrollStartedRef.current = true;
        // Mark the scroll as started
        onChange(previousValueNumber, {
          changeType: CHANGE_META_TYPE.CHANGE_START,
        });
      }

      const steppedValueString = getSteppedValue(
        previousValueString,
        direction
      );
      // Update the input value directly
      internalRef.current.value = steppedValueString;
      const steppedValueNumber = utilities.parseNumber(steppedValueString);

      // Use safe clamp to handle potential NaN values
      const safeSteppedValue = safeClampInBoundsValue(steppedValueNumber);

      // Trigger `change` for the current value
      // The change type is hardcoded to 'change' for wheel events
      onChange(safeSteppedValue, {
        changeType: CHANGE_META_TYPE.CHANGE,
      });

      queueChangeEndMeta(
        safeSteppedValue,
        () => (scrollStartedRef.current = false)
      );
    },
    [
      containsMathExpression,
      getSteppedValue,
      isDisabled,
      max,
      min,
      onChange,
      queueChangeEndMeta,
      safeClampInBoundsValue,
      shouldAllowWheelAdjustment,
    ]
  );

  const handleArrowKeydown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, isSkip: boolean) => {
      if (isDisabled || !internalRef.current) return;

      const currentValueString = internalRef.current.value;

      // Don't allow arrow key stepping when input contains math expressions
      if (containsMathExpression(currentValueString)) {
        return;
      }

      // Determine the direction based on the key pressed
      let direction: StepDirection | null = null;

      if (event.key === ALLOWED_KEYS.ARROW_UP) {
        event.preventDefault();
        direction = STEP_DIRECTIONS.UP;
      } else if (event.key === ALLOWED_KEYS.ARROW_DOWN) {
        event.preventDefault();
        direction = STEP_DIRECTIONS.DOWN;
      } else {
        return;
      }

      // Calculate the new value
      const newValueString = getSteppedValue(
        currentValueString,
        direction,
        isSkip
      );

      internalRef.current.value = newValueString;
      const newValueNumber = utilities.parseNumber(newValueString);

      // Use safe clamp to handle potential NaN values
      const safeValue = safeClampInBoundsValue(newValueNumber);

      onChange(safeValue, {
        changeType: CHANGE_META_TYPE.CHANGE,
      });
    },
    [
      containsMathExpression,
      getSteppedValue,
      isDisabled,
      onChange,
      safeClampInBoundsValue,
    ]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>, isSkip: boolean) => {
      const key = event.key;
      const input = event.currentTarget;
      const isNumber = /^[0-9]$/.test(key);
      const isAllowedKey = allowedKeysSet.has(key);
      const isModifierKey = event.ctrlKey || event.metaKey; // Detect Ctrl or Cmd

      if (event.key === 'Enter') {
        const inputValue = event.currentTarget.value;

        if (inputValue === '') {
          if (placeholder) {
            onChange(undefined, {changeType: CHANGE_META_TYPE.CHANGE});
            internalRef.current!.value = '';
          }
          return;
        }

        // First try to evaluate as a math expression
        const mathResult = utilities.evaluateMathExpression(inputValue);
        let finalValue: number;

        if (!isNaN(mathResult)) {
          // Math expression was valid, use the result
          finalValue = mathResult;
        } else {
          // Fall back to parsing as a simple number
          finalValue = utilities.parseNumber(inputValue);
        }

        const clampedValue = safeClampInBoundsValue(finalValue);
        event.currentTarget.value = String(clampedValue);
        internalRef.current!.value = String(clampedValue);

        commitValue(clampedValue, CHANGE_META_TYPE.CHANGE);
        return;
      }

      if (
        (isModifierKey && key === 'r') ||
        (isModifierKey && key === 'R') ||
        (isModifierKey && key === 'z') ||
        (isModifierKey && key === 'Z') ||
        (isModifierKey && key === 'a') ||
        (isModifierKey && key === 'c') ||
        (isModifierKey && key === 'v') ||
        (isModifierKey && key === 'x')
      ) {
        return;
      }

      // Allow '-' for negative numbers at start OR for subtraction anywhere
      // Only prevent '-' if it would create invalid patterns like '--' or '+-'
      if (key === ALLOWED_KEYS.MINUS_SIGN) {
        const currentValue = input.value;
        const cursorPosition = input.selectionStart || 0;

        // Check the character before the cursor position
        const charBefore = currentValue[cursorPosition - 1];

        // Prevent consecutive operators (like --, +-, *-, /-)
        if (charBefore && ['+', '-', '*', '/'].includes(charBefore)) {
          event.preventDefault();
          return;
        }
      }

      // Allow '.' if not already present
      if (
        key === ALLOWED_KEYS.DECIMAL &&
        input.value.includes(ALLOWED_KEYS.DECIMAL)
      ) {
        event.preventDefault();
        return;
      }

      // Allow ',' if not already present and no '.' is present
      if (
        key === ALLOWED_KEYS.COMMA &&
        (input.value.includes(ALLOWED_KEYS.COMMA) ||
          input.value.includes(ALLOWED_KEYS.DECIMAL))
      ) {
        event.preventDefault();
        return;
      }

      // Allow mathematical operators for expression evaluation
      const isMathOperator = [
        ALLOWED_KEYS.MULTIPLY,
        ALLOWED_KEYS.DIVIDE,
        ALLOWED_KEYS.PLUS,
        ALLOWED_KEYS.OPEN_PAREN,
        ALLOWED_KEYS.CLOSE_PAREN,
      ].includes(key);

      if (!isNumber && !isAllowedKey && !isMathOperator) {
        event.preventDefault();
        return;
      }

      handleArrowKeydown(event, isSkip);
    },
    [
      safeClampInBoundsValue,
      commitValue,
      handleArrowKeydown,
      onChange,
      placeholder,
    ]
  );

  const handleSetIsScrubbing = React.useCallback(
    (_isScrubbing: boolean) => {
      setIsScrubbing(_isScrubbing);
      if (!_isScrubbing) {
        scrubStartedRef.current = false; // Reset when scrubbing ends
      }
    },
    [setIsScrubbing]
  );

  // Scrubbing allows changing the value when the user presses [option] + horizontal mouse drag
  const handleScrubChange = React.useCallback(
    (direction: ScrubDirection, isSkip: boolean) => {
      if (isDisabled || !internalRef.current) return;

      const mappedDirection = SCRUB_TO_STEP_MAP[direction];
      const currentValueString = internalRef.current.value;
      const hasMathExpression = containsMathExpression(currentValueString);
      const previousValueNumber = utilities.parseNumber(currentValueString);

      // Don't allow scrubbing when input contains math expressions or if the value is already at the min or max
      if (
        hasMathExpression ||
        (mappedDirection === STEP_DIRECTIONS.UP &&
          previousValueNumber >= max) ||
        (mappedDirection === STEP_DIRECTIONS.DOWN && previousValueNumber <= min)
      ) {
        return;
      }

      const newValueString = getSteppedValue(
        currentValueString,
        mappedDirection,
        isSkip
      );

      internalRef.current.value = newValueString;
      const newValueNumber = utilities.parseNumber(newValueString);

      // Use safe clamp to handle potential NaN values
      const safeValue = safeClampInBoundsValue(newValueNumber);

      // Only fire CHANGE_START if this is the first scrub event in the session
      if (!scrubStartedRef.current) {
        onChange(safeValue, {
          changeType: CHANGE_META_TYPE.CHANGE_START,
        });

        scrubStartedRef.current = true;
      }

      if (isScrubbing) {
        onChange(safeValue, {
          changeType: CHANGE_META_TYPE.CHANGE,
        });
      }

      queueChangeEndMeta(safeValue);
    },
    [
      containsMathExpression,
      getSteppedValue,
      isDisabled,
      isScrubbing,
      max,
      min,
      onChange,
      queueChangeEndMeta,
      safeClampInBoundsValue,
    ]
  );

  const mergedRefs = mergeRefs([
    internalRef,
    useMouseWheel(handleWheelEvent),
    externalRef,
  ]);

  // Memoize the state and handlers objects to avoid recreating them on each render
  const state = React.useMemo(
    () => ({
      _currentValue: value,
      isDisabled,
      isError,
      isFocused,
      isMax,
      isMin,
      isScrubbing,
      max,
      min,
      name,
      placeholder,
      prefix,
      shouldAllowWheelAdjustment,
      shouldShowTrailingControls,
      step,
      suffix,
      variant,
      ref: mergedRefs,
    }),
    [
      value,
      isDisabled,
      isError,
      isFocused,
      isMax,
      isMin,
      isScrubbing,
      max,
      min,
      name,
      placeholder,
      prefix,
      shouldAllowWheelAdjustment,
      shouldShowTrailingControls,
      step,
      suffix,
      variant,
      mergedRefs,
    ]
  );

  const handlers = React.useMemo(
    () => ({
      onBlur: handleOnBlur,
      onChange: handleValueChange,
      onFocus: handleOnFocus,
      onKeyDown: handleKeyDown,
      onScrubChange: handleScrubChange,
      onStepClick: handleStepClick,
      onSetIsScrubbing: handleSetIsScrubbing,
    }),
    [
      handleOnBlur,
      handleValueChange,
      handleOnFocus,
      handleKeyDown,
      handleScrubChange,
      handleStepClick,
      handleSetIsScrubbing,
    ]
  );

  return {state, handlers};
};
